import React, { useState } from 'react';
import { AlertTriangle, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';

const APP_URL = 'https://meal-insights-pro.emergent.host/auth/email';

function detectInAppBrowser() {
  const ua = navigator.userAgent || navigator.vendor || '';
  // Messenger
  if (/FBAN|FBAV|FB_IAB|Messenger/i.test(ua)) return 'messenger';
  // Facebook
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'facebook';
  // Instagram
  if (/Instagram/i.test(ua)) return 'instagram';
  // LinkedIn
  if (/LinkedInApp/i.test(ua)) return 'linkedin';
  // Twitter/X
  if (/Twitter/i.test(ua)) return 'twitter';
  // Snapchat
  if (/Snapchat/i.test(ua)) return 'snapchat';
  // TikTok
  if (/BytedanceWebview|TikTok/i.test(ua)) return 'tiktok';
  // Generic WebView detection (Android)
  if (/wv\)|WebView/i.test(ua) && /Android/i.test(ua)) return 'webview';
  return null;
}

function getInstructions(browser) {
  const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);

  if (browser === 'messenger' || browser === 'facebook') {
    return isIOS
      ? 'Appuie sur les 3 points (...) en bas a droite, puis choisis "Ouvrir dans Safari".'
      : 'Appuie sur les 3 points (...) en haut a droite, puis choisis "Ouvrir dans le navigateur".';
  }
  if (browser === 'instagram') {
    return isIOS
      ? 'Appuie sur les 3 points (...) en haut a droite, puis choisis "Ouvrir dans Safari".'
      : 'Appuie sur les 3 points (...) en haut a droite, puis choisis "Ouvrir dans Chrome".';
  }
  if (browser === 'linkedin') {
    return 'Appuie sur les 3 points (...) en haut a droite, puis choisis "Ouvrir dans le navigateur".';
  }
  return isIOS
    ? 'Ouvre ce lien directement dans Safari pour une experience optimale.'
    : 'Ouvre ce lien directement dans Chrome pour une experience optimale.';
}

const BROWSER_LABELS = {
  messenger: 'Messenger',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'Twitter/X',
  snapchat: 'Snapchat',
  tiktok: 'TikTok',
  webview: 'un navigateur integre',
};

export default function InAppBrowserBlocker({ children }) {
  const [copied, setCopied] = useState(false);
  const browser = detectInAppBrowser();

  if (!browser) return children;

  const browserLabel = BROWSER_LABELS[browser] || browser;
  const instructions = getInstructions(browser);
  const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const input = document.createElement('input');
      input.value = APP_URL;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4" data-testid="inapp-browser-blocker">
      <div className="max-w-md w-full space-y-6 text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <img src="/logo192.png" alt="YUMIX" className="w-12 h-12 rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="font-heading text-2xl uppercase tracking-tight text-white">YUMIX</span>
        </div>

        {/* Warning card */}
        <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-2xl p-6 space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
          </div>

          <h1 className="font-heading text-xl uppercase tracking-tight text-white">
            Navigateur non supporte
          </h1>

          <p className="text-sm text-zinc-300 leading-relaxed">
            Tu es actuellement dans <strong className="text-amber-400">{browserLabel}</strong>.
            Pour installer YUMIX et profiter de toutes les fonctionnalites, ouvre le lien dans
            <strong className="text-white"> {isIOS ? 'Safari' : 'Chrome'}</strong>.
          </p>

          {/* Instructions */}
          <div className="bg-black/30 rounded-xl p-4 text-left">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-2">
              Comment faire :
            </p>
            <p className="text-sm text-zinc-300">{instructions}</p>
          </div>
        </div>

        {/* URL Copy section */}
        <div className="bg-zinc-900/80 border border-zinc-700 rounded-2xl p-5 space-y-3">
          <p className="text-xs text-zinc-400">
            Ou copie ce lien et colle-le dans {isIOS ? 'Safari' : 'Chrome'} :
          </p>

          <div className="bg-black/50 rounded-lg px-3 py-2.5 flex items-center gap-2 border border-zinc-700">
            <ExternalLink className="w-4 h-4 text-primary shrink-0" />
            <span className="text-xs text-zinc-300 truncate flex-1 select-all font-mono">
              {APP_URL}
            </span>
          </div>

          <button
            onClick={handleCopy}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-heading uppercase text-sm transition-all ${
              copied
                ? 'bg-green-600 text-white'
                : 'bg-primary text-black hover:bg-primary/90'
            }`}
            data-testid="copy-url-btn"
          >
            {copied ? (
              <><CheckCircle2 className="w-5 h-5" /> Lien copie !</>
            ) : (
              <><Copy className="w-5 h-5" /> Copier le lien</>
            )}
          </button>
        </div>

        {/* Try anyway link */}
        <a
          href={APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          data-testid="try-anyway-link"
        >
          <ExternalLink className="w-3 h-3" />
          Essayer quand meme (non recommande)
        </a>
      </div>
    </div>
  );
}

export { detectInAppBrowser };
